import React, { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import type { AlignmentOutletContext } from "../AlignmentModule";
import { GRI_CRITERIA, GRI_THEMES, type CriterionStatus, type CriterionLevel } from "../norms/normConfig";
import WorkInProgress from "../WorkInProgress";
import "./DashboardPage.css";

/* ── Status / level config ── */
const STATUS_CONFIG: Record<CriterionStatus, { label: string; color: string }> = {
  "A verifier":     { label: "A vérifier",    color: "#e11d48" },
  "Conforme":       { label: "Conforme",       color: "#16a34a" },
  "Partiel":        { label: "Partiel",        color: "#d97706" },
  "Non conforme":   { label: "Non conforme",   color: "#dc2626" },
  "Non applicable": { label: "Non applicable", color: "#9ca3af" },
};
const LEVEL_CONFIG: Record<CriterionLevel, { color: string }> = {
  Critique: { color: "#e11d48" },
  Majeur:   { color: "#d97706" },
  Standard: { color: "#3b82f6" },
};
const LEVEL_WEIGHTS: Record<CriterionLevel, number> = { Critique: 3, Majeur: 2, Standard: 1 };
const STATUS_SCORES: Record<CriterionStatus, number> = {
  "Conforme": 1, "Partiel": 0.5, "Non conforme": 0, "A verifier": 0, "Non applicable": 0,
};

const THEME_ICONS: Record<string, string> = {
  "Standards Universels":    "🌐",
  "Environnement":           "🌱",
  "Social et Droits Humains":"🤝",
  "Gouvernance & Éthique":   "⚖️",
};

/* ── Donut chart ── */
interface DonutSegment { label: string; value: number; color: string; }
const DonutChart: React.FC<{
  segments: DonutSegment[];
  centerValue: string | number;
  centerLabel: string;
  size?: number;
}> = ({ segments, centerValue, centerLabel, size = 180 }) => {
  const cx = size / 2, cy = size / 2;
  const r = 62, sw = 22;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0);

  const segs: React.ReactElement[] = [];
  let rotation = -90;
  segments.forEach((seg, i) => {
    if (seg.value === 0) return;
    const pct = seg.value / total;
    const dash = pct * c;
    segs.push(
      <circle
        key={i}
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke={seg.color}
        strokeWidth={sw}
        strokeDasharray={`${dash} ${c - dash}`}
        style={{
          transform: `rotate(${rotation}deg)`,
          transformOrigin: `${cx}px ${cy}px`,
          transition: "stroke-dasharray 0.5s ease",
        }}
      />
    );
    rotation += pct * 360;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={sw} />
      {total > 0 ? segs : null}
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize="22" fontWeight="700" fill="#111827">
        {centerValue}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="11" fill="#6b7280">
        {centerLabel}
      </text>
    </svg>
  );
};

/* ── Level / Status badges ── */
const LevelBadge: React.FC<{ level: CriterionLevel }> = ({ level }) => (
  <span className="db-badge" style={{
    background: `${LEVEL_CONFIG[level].color}18`,
    color: LEVEL_CONFIG[level].color,
    borderColor: `${LEVEL_CONFIG[level].color}30`,
  }}>
    {level}
  </span>
);
const StatusBadge: React.FC<{ status: CriterionStatus }> = ({ status }) => (
  <span className="db-badge" style={{
    background: `${STATUS_CONFIG[status].color}14`,
    color: STATUS_CONFIG[status].color,
    borderColor: `${STATUS_CONFIG[status].color}28`,
  }}>
    {STATUS_CONFIG[status].label}
  </span>
);

const DashboardPage: React.FC = () => {
  const { selectedNorm, criteriaStates } = useOutletContext<AlignmentOutletContext>();

  if (selectedNorm !== "GRI") return <WorkInProgress normLabel={selectedNorm} />;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const stats = useMemo(() => {
    const maxScore = GRI_CRITERIA.reduce((s, c) => s + LEVEL_WEIGHTS[c.level], 0);
    const actualScore = GRI_CRITERIA.reduce((s, c) => {
      const st = criteriaStates[c.id]?.status ?? "A verifier";
      return s + LEVEL_WEIGHTS[c.level] * STATUS_SCORES[st];
    }, 0);
    const score = Math.round((actualScore / maxScore) * 100);

    const totalRequired = GRI_CRITERIA.reduce((s, c) => s + c.requiredDocs, 0);
    const totalUploaded = GRI_CRITERIA.reduce(
      (s, c) => s + (criteriaStates[c.id]?.uploadedFiles.length ?? 0), 0
    );

    const statusCounts: Record<CriterionStatus, number> = {
      "A verifier": 0, "Conforme": 0, "Partiel": 0, "Non conforme": 0, "Non applicable": 0,
    };
    const levelCounts: Record<CriterionLevel, number> = { Critique: 0, Majeur: 0, Standard: 0 };
    GRI_CRITERIA.forEach((c) => {
      const st = criteriaStates[c.id]?.status ?? "A verifier";
      statusCounts[st]++;
      levelCounts[c.level]++;
    });

    const themeStats = GRI_THEMES.map((theme) => {
      const criteria = GRI_CRITERIA.filter((c) => c.theme === theme);
      const total = criteria.length;
      const compliant = criteria.filter((c) => criteriaStates[c.id]?.status === "Conforme").length;
      const partial = criteria.filter((c) => criteriaStates[c.id]?.status === "Partiel").length;
      const pct = total > 0 ? Math.round(((compliant + partial * 0.5) / total) * 100) : 0;
      const maxDocs = criteria.reduce((s, c) => s + c.requiredDocs, 0);
      const uploadedDocs = criteria.reduce((s, c) => s + (criteriaStates[c.id]?.uploadedFiles.length ?? 0), 0);
      return { theme, total, compliant, partial, pct, maxDocs, uploadedDocs };
    });

    return { score, totalRequired, totalUploaded, statusCounts, levelCounts, themeStats };
  }, [criteriaStates]);

  const total = GRI_CRITERIA.length;

  const statusSegments = (Object.entries(stats.statusCounts) as [CriterionStatus, number][]).map(
    ([status, value]) => ({ label: STATUS_CONFIG[status].label, value, color: STATUS_CONFIG[status].color })
  );
  const levelSegments = (Object.entries(stats.levelCounts) as [CriterionLevel, number][]).map(
    ([level, value]) => ({ label: level, value, color: LEVEL_CONFIG[level].color })
  );

  return (
    <div className="db-page">
      <div className="db-page-header">
        <h1 className="db-page-title">Dashboard GRI</h1>
        <p className="db-page-desc">Suivi de votre alignement avec les Standards GRI.</p>
      </div>

      {/* ── Vue globale ── */}
      <section className="db-section">
        <div className="db-section-title">
          <span className="db-section-bar" />
          Vue globale
        </div>
        <div className="db-global-cards">
          <div className="db-global-card">
            <div className="db-global-card-label">
              SCORE SIMULÉ
              <button className="db-help-btn" title="Score pondéré selon la criticité des critères"><HelpCircle size={13} /></button>
            </div>
            <div className="db-global-card-value">{stats.score}<span className="db-global-card-total">/100</span></div>
            <div className="db-global-card-sub">score pondéré par criticité</div>
            <div className="db-score-bar">
              <div className="db-score-bar-fill" style={{ width: `${stats.score}%`, background: stats.score >= 70 ? "#16a34a" : stats.score >= 40 ? "#d97706" : "#e11d48" }} />
            </div>
          </div>
          <div className="db-global-card">
            <div className="db-global-card-label">SUIVI DOCUMENTATION</div>
            <div className="db-global-card-value">
              {stats.totalUploaded}<span className="db-global-card-total">/ {stats.totalRequired}</span>
            </div>
            <div className="db-global-card-sub">{stats.totalRequired - stats.totalUploaded} manquants</div>
            <div className="db-score-bar">
              <div
                className="db-score-bar-fill"
                style={{
                  width: `${stats.totalRequired > 0 ? Math.round((stats.totalUploaded / stats.totalRequired) * 100) : 0}%`,
                  background: "#5b3fa6",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Etat des exigences ── */}
      <section className="db-section">
        <div className="db-section-title">
          <span className="db-section-bar" />
          État des exigences
        </div>
        <div className="db-charts-row">
          {/* Status donut */}
          <div className="db-chart-card">
            <div className="db-chart-title">Répartition des statuts</div>
            <div className="db-chart-inner">
              <DonutChart
                segments={statusSegments}
                centerValue={total}
                centerLabel="critères"
              />
              <div className="db-chart-legend">
                {(Object.entries(stats.statusCounts) as [CriterionStatus, number][]).map(([status, count]) => (
                  <div key={status} className="db-legend-row">
                    <span className="db-legend-dot" style={{ background: STATUS_CONFIG[status].color }} />
                    <span className="db-legend-label">{STATUS_CONFIG[status].label}</span>
                    <span className="db-legend-value">
                      {count} | {total > 0 ? Math.round((count / total) * 100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Level donut */}
          <div className="db-chart-card">
            <div className="db-chart-title">Répartition des niveaux</div>
            <div className="db-chart-inner">
              <DonutChart
                segments={levelSegments}
                centerValue={total}
                centerLabel="critères"
              />
              <div className="db-chart-legend">
                {(Object.entries(stats.levelCounts) as [CriterionLevel, number][]).map(([level, count]) => (
                  <div key={level} className="db-legend-row">
                    <span className="db-legend-dot" style={{ background: LEVEL_CONFIG[level].color }} />
                    <span className="db-legend-label">{level}</span>
                    <span className="db-legend-value">
                      {count} | {total > 0 ? Math.round((count / total) * 100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Avancement par thème ── */}
      <section className="db-section">
        <div className="db-section-title">
          <span className="db-section-bar" />
          Avancement par thème
          <button className="db-help-btn" title="Pourcentage de critères conformes ou partiellement conformes"><HelpCircle size={13} /></button>
        </div>
        <div className="db-theme-cards">
          {stats.themeStats.map(({ theme, total: tTotal, pct, maxDocs, uploadedDocs }) => (
            <div key={theme} className="db-theme-card">
              <div className="db-theme-card-header">
                <span className="db-theme-icon">{THEME_ICONS[theme]}</span>
                <span className="db-theme-name">{theme}</span>
                <span className="db-theme-pct">{pct}%</span>
              </div>
              <div className="db-theme-stat">{pct}% | 0/{tTotal}</div>
              <div className="db-progress-bar">
                <div
                  className="db-progress-fill"
                  style={{
                    width: `${pct}%`,
                    background: pct >= 70 ? "#16a34a" : pct >= 40 ? "#d97706" : "#e11d48",
                  }}
                />
              </div>
              <div className="db-theme-docs">
                📎 {uploadedDocs}/{maxDocs} docs
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Avancement par critère ── */}
      <section className="db-section">
        <div className="db-section-title">
          <span className="db-section-bar" />
          Avancement par critère
          <button className="db-help-btn" title="Vue par thème de l'état de chaque critère GRI"><HelpCircle size={13} /></button>
        </div>
        <div className="db-criteria-themes">
          {GRI_THEMES.map((theme) => {
            const criteria = GRI_CRITERIA.filter((c) => c.theme === theme);
            return (
              <div key={theme} className="db-criteria-theme-col">
                <div className="db-criteria-theme-title">
                  {THEME_ICONS[theme]} {theme}
                </div>
                {criteria.map((c) => {
                  const st = criteriaStates[c.id]?.status ?? "A verifier";
                  const uploaded = criteriaStates[c.id]?.uploadedFiles.length ?? 0;
                  return (
                    <div key={c.id} className="db-criterion-card">
                      <div className="db-criterion-card-top">
                        <span className="db-criterion-code">{c.code}</span>
                        <LevelBadge level={c.level} />
                        <StatusBadge status={st} />
                      </div>
                      <div className="db-criterion-name">{c.name}</div>
                      <div className="db-criterion-docs-bar">
                        <div
                          className="db-criterion-docs-fill"
                          style={{ width: `${Math.round((uploaded / c.requiredDocs) * 100)}%` }}
                        />
                      </div>
                      <div className="db-criterion-docs-label">
                        {uploaded}/{c.requiredDocs} docs
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
